import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Card,
  Flex,
  Button,
  Badge,
  Table,
  DataList,
  IconButton,
  Spinner,
  TextField,
  TextArea,
  Dialog,
  Select,
  Separator,
} from '@radix-ui/themes';
import {
  TransactionFingerprint,
  FingerprintValueType,
} from '../../types';
import {
  BellIcon,
  ArrowLeftIcon,
  PlayIcon,
  PauseIcon,
  Pencil1Icon,
  CalendarIcon,
  LightningBoltIcon,
  ClockIcon,
  PlusIcon,
  CheckIcon,
  Cross2Icon,
  DiscordLogoIcon,
  Link2Icon,
  EnvelopeClosedIcon,
  ActivityLogIcon,
  ExternalLinkIcon,
  TargetIcon,
  ChatBubbleIcon,
} from '@radix-ui/react-icons';
import HashShortener from '../../components/HashShortener';
import BlockNumberDisplay from '../../components/BlockNumberDisplay';
import TimeAgoTooltip from '../../components/TimeAgoTooltip';
import DiscordBotSelect from '../../components/DiscordBotSelect';
import DiscordBotChannelSelect from '../../components/DiscordBotChannelSelect';
import WebhookSelect from '../../components/WebhookSelect';
import DetailPageLayout from '../../components/DetailPageLayout';
import { useGetAlertQuery } from '../../graphql/queries/GetAlert.generated';
import { useUpdateAlertMutation } from '../../graphql/mutations/UpdateAlert.generated';
import { AlertStatus, AlertDestinationType } from '../../types';

const StatusBadge = ({ status }: { status: AlertStatus }) => {
  const config = {
    [AlertStatus.Active]: { color: 'green' as const, label: 'Active' },
    [AlertStatus.Paused]: { color: 'amber' as const, label: 'Paused' },
    [AlertStatus.Draft]: { color: 'gray' as const, label: 'Draft' },
  };

  const { color, label } = config[status] || config[AlertStatus.Draft];

  return (
    <Badge color={color} variant="soft" size="2">
      {label}
    </Badge>
  );
};

const getDestinationLabel = (destination: {
  destinationType: AlertDestinationType;
  discordChannelId?: string | null;
  target?:
  | {
    __typename?: 'DiscordBot';
    botUsername?: string | null;
    guildName?: string | null;
  }
  | { __typename?: 'Webhook'; label: string; url: string }
  | null;
}): string => {
  if (
    destination.destinationType === AlertDestinationType.Discord &&
    destination.target
  ) {
    const discordBot = destination.target as {
      botUsername?: string | null;
      guildName?: string | null;
    };
    const guildName = discordBot.guildName || 'Unknown Server';
    return `Discord: ${guildName}${destination.discordChannelId ? ` #${destination.discordChannelId}` : ''}`;
  }
  if (
    destination.destinationType === AlertDestinationType.Webhook &&
    destination.target
  ) {
    const webhook = destination.target as { label: string };
    return `Webhook: ${webhook.label}`;
  }
  if (destination.destinationType === AlertDestinationType.Slack) {
    return 'Slack';
  }
  if (destination.destinationType === AlertDestinationType.Email) {
    return 'Email';
  }
  return destination.destinationType;
};

const formatFingerprintCondition = (
  fingerprint: TransactionFingerprint,
): React.ReactNode => {
  const field = fingerprint.field.toLowerCase().replace(/_/g, ' ');
  const operation = fingerprint.operation.replace(/_/g, ' ').toUpperCase();

  let value: string;
  if (
    fingerprint.value.valueType === FingerprintValueType.MonetaryValue &&
    fingerprint.value.value
  ) {
    const { amount, currency } = fingerprint.value.value;
    if (amount !== undefined && currency) {
      value = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } else {
      value = JSON.stringify(fingerprint.value.value);
    }
  } else {
    value = JSON.stringify(fingerprint.value.value);
  }

  return (
    <>
      {field}{' '}
      <Text
        as="span"
        weight="bold"
        style={{ color: 'var(--cyan-11)' }}
      >
        {operation}
      </Text>{' '}
      {value}
    </>
  );
};

const AlertDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useGetAlertQuery({
    variables: { id: id || '' },
    skip: !id,
  });
  const [updateAlert, { loading: updating }] = useUpdateAlertMutation();

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // Add destination dialog state
  const [isAddDestinationOpen, setIsAddDestinationOpen] = useState(false);
  const [destinationType, setDestinationType] = useState<'discord' | 'webhook'>(
    'discord',
  );
  const [selectedBotId, setSelectedBotId] = useState<string>('');
  const [selectedChannelId, setSelectedChannelId] = useState<string>('');
  const [selectedWebhookId, setSelectedWebhookId] = useState<string>('');

  const alert = data?.alert;

  const handleToggleStatus = async () => {
    if (!alert) return;
    const newStatus =
      alert.status === AlertStatus.Active
        ? AlertStatus.Paused
        : AlertStatus.Active;
    await updateAlert({
      variables: { id: alert.id, input: { status: newStatus } },
    });
    refetch();
  };

  const handleStartEdit = () => {
    if (!alert) return;
    setEditName(alert.name);
    setEditDescription(alert.description || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditName('');
    setEditDescription('');
  };

  const handleSaveEdit = async () => {
    if (!alert || !editName.trim()) return;
    await updateAlert({
      variables: {
        id: alert.id,
        input: {
          name: editName.trim(),
          description: editDescription.trim() || undefined,
        },
      },
    });
    setIsEditing(false);
    refetch();
  };

  const handleAddDestination = async () => {
    if (!alert) return;

    // Build the new destination based on type
    const newDestination: any = {
      destinationType:
        destinationType === 'discord'
          ? AlertDestinationType.Discord
          : AlertDestinationType.Webhook,
    };

    if (destinationType === 'discord') {
      if (!selectedBotId || !selectedChannelId) return;
      newDestination.discordBotId = selectedBotId;
      newDestination.discordChannelId = selectedChannelId;
    } else {
      if (!selectedWebhookId) return;
      newDestination.webhookId = selectedWebhookId;
    }

    // Get existing destinations and add the new one
    const existingDestinations = alert.destinations.map((dest) => ({
      destinationType: dest.destinationType,
      discordBotId: dest.discordBotId || undefined,
      discordChannelId: dest.discordChannelId || undefined,
      webhookId: dest.webhookId || undefined,
      slackInstallationId: dest.slackInstallationId || undefined,
      slackChannelId: dest.slackChannelId || undefined,
    }));

    // Update the alert with all destinations
    await updateAlert({
      variables: {
        id: alert.id,
        input: {
          destinations: [...existingDestinations, newDestination],
        },
      },
    });

    setIsAddDestinationOpen(false);
    setSelectedBotId('');
    setSelectedChannelId('');
    setSelectedWebhookId('');
    refetch();
  };

  const resetDestinationDialog = () => {
    setDestinationType('discord');
    setSelectedBotId('');
    setSelectedChannelId('');
    setSelectedWebhookId('');
  };

  if (loading) {
    return (
      <Flex align="center" justify="center" py="9">
        <Spinner size="3" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex align="center" justify="center" py="9">
        <Text color="red">Error loading alert: {error.message}</Text>
      </Flex>
    );
  }

  if (!alert) {
    return (
      <Flex align="center" justify="center" direction="column" gap="4" py="9">
        <Text size="4" color="gray">
          Alert not found
        </Text>
        <Button variant="soft" onClick={() => navigate('/alerts')}>
          <ArrowLeftIcon />
          Back to Alerts
        </Button>
      </Flex>
    );
  }

  const canAddDestination =
    destinationType === 'discord'
      ? selectedBotId && selectedChannelId
      : selectedWebhookId;

  return (
    <DetailPageLayout
      backPath="/alerts"
      backText="Back to Alerts"
      icon={<BellIcon width={24} height={24} />}
      iconBackgroundColor="var(--amber-3)"
      iconColor="var(--amber-11)"
      title={alert.name}
      subtitle={alert.description}
      badge={<StatusBadge status={alert.status} />}
      actions={
        <Button
          variant="solid"
          onClick={() => navigate(`/alerts/${alert.id}/edit`)}
        >
          <Pencil1Icon />
          Edit Alert
        </Button>
      }
    >
      <Box>
        {/* Status Banner - Full Width */}
        <Box
          mb="4"
          style={{
            backgroundColor: 'var(--gray-2)',
            marginLeft: 'calc(-12px - var(--space-1))',
            marginRight: 'calc(-12px - var(--space-1))',
          }}
        >
          <Box px="4" py="4">
            <Flex
              align="center"
              justify="between"
              gap="6"
              style={{ maxWidth: '100%' }}
            >
              <Flex align="center" gap="3" style={{ flex: '1' }}>
                <Box
                  style={{
                    padding: '10px',
                    borderRadius: '6px',
                    backgroundColor: 'var(--gray-1)',
                    border: '1px solid var(--gray-6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CalendarIcon width={18} height={18} />
                </Box>
                <Flex direction="column" gap="1">
                  <Text size="1" weight="medium" color="gray">
                    Created
                  </Text>
                  <Text size="2" weight="bold">
                    <TimeAgoTooltip value={alert.createdAt} />
                  </Text>
                </Flex>
              </Flex>

              <Flex align="center" gap="3" style={{ flex: '1' }}>
                <Box
                  style={{
                    padding: '10px',
                    borderRadius: '6px',
                    backgroundColor: 'var(--gray-1)',
                    border: '1px solid var(--gray-6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ClockIcon width={18} height={18} />
                </Box>
                <Flex direction="column" gap="1">
                  <Text size="1" weight="medium" color="gray">
                    Last Triggered
                  </Text>
                  <Text size="2" weight="bold">
                    {alert.lastTriggeredAt ? (
                      <TimeAgoTooltip value={alert.lastTriggeredAt} />
                    ) : (
                      'Never'
                    )}
                  </Text>
                </Flex>
              </Flex>

              <Flex align="center" gap="3" style={{ flex: '1' }}>
                <Box
                  style={{
                    padding: '10px',
                    borderRadius: '6px',
                    backgroundColor: 'var(--gray-1)',
                    border: '1px solid var(--gray-6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ActivityLogIcon width={18} height={18} />
                </Box>
                <Flex direction="column" gap="1">
                  <Text size="1" weight="medium" color="gray">
                    Total Triggers
                  </Text>
                  <Text size="2" weight="bold">
                    {alert.triggerCount.toLocaleString()}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Box>
        </Box>

        {/* Trigger History */}
        <Box mb="4">
          <Card size="2">
            <Flex align="center" justify="between" mb="3">
              <Box>
                <Heading as="h2" size="4" weight="medium" mb="1">
                  Trigger History
                </Heading>
                <Text size="2" color="gray">
                  Last 7 days of alert activity
                </Text>
              </Box>
            </Flex>
            <Box mb="3" style={{ marginLeft: 'calc(-1 * var(--space-4))', marginRight: 'calc(-1 * var(--space-4))', borderBottom: '1px solid var(--gray-6)' }} />
            <Flex
              align="center"
              justify="center"
              style={{ height: '200px', backgroundColor: 'var(--gray-2)' }}
            >
              <Text size="2" color="gray">
                Chart visualization coming soon
              </Text>
            </Flex>
          </Card>
        </Box>

        {/* Linked Signal */}
        {alert.signal && (
          <Box mb="4">
            <Card size="2">
              <Flex align="center" justify="between" mb="3">
                <Box>
                  <Heading as="h2" size="4" weight="medium" mb="1">
                    Linked Signal
                  </Heading>
                  <Text size="2" color="gray">
                    Alert triggers when this signal matches
                  </Text>
                </Box>
              </Flex>
              <Box mb="3" style={{ marginLeft: 'calc(-1 * var(--space-4))', marginRight: 'calc(-1 * var(--space-4))', borderBottom: '1px solid var(--gray-6)' }} />

              <Card
                onClick={() => navigate(`/signals/${alert.signal?.id}`)}
                style={{
                  backgroundColor: 'var(--gray-3)',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
              >
                <Flex direction="column" gap="3">
                  <Flex align="center" justify="between" gap="3">
                    <Flex align="center" gap="3">
                      <Box
                        style={{
                          padding: '10px',
                          borderRadius: '6px',
                          backgroundColor: 'var(--purple-3)',
                          color: 'var(--purple-11)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <TargetIcon width={18} height={18} />
                      </Box>
                      <Flex direction="column" gap="1">
                        <Badge color="purple" variant="soft" size="1" style={{ width: 'fit-content' }}>
                          {alert.signal.signalType}
                        </Badge>
                        <Heading size="3" weight="medium">
                          {alert.signal.name}
                        </Heading>
                      </Flex>
                    </Flex>
                    <IconButton variant="ghost" color="gray" size="2">
                      <ExternalLinkIcon width={16} height={16} />
                    </IconButton>
                  </Flex>
                  {alert.signal.fingerprints && alert.signal.fingerprints.length > 0 && (
                    <>
                      <Separator size="4" style={{ width: '100%' }} />
                      <Box>
                        <Text size="1" weight="medium" color="gray" mb="1" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Conditions
                        </Text>
                        <Flex direction="column" gap="1">
                          {alert.signal.fingerprints.map((fingerprint: any, idx: number) => (
                            <Text key={idx} size="2" color="gray">
                              {formatFingerprintCondition(fingerprint)}
                            </Text>
                          ))}
                        </Flex>
                      </Box>
                    </>
                  )}
                </Flex>
              </Card>
            </Card>
          </Box>
        )}

        {/* Destinations */}
        <Box mb="4">
          <Card size="2">
            <Flex align="center" justify="between" mb="3">
              <Box>
                <Heading as="h2" size="4" weight="medium" mb="1">
                  Destinations
                </Heading>
                <Text size="2" color="gray">
                  Where to send notifications when alert triggers
                </Text>
              </Box>
            </Flex>
            <Box
              mb="3"
              style={{
                marginLeft: 'calc(-1 * var(--space-4))',
                marginRight: 'calc(-1 * var(--space-4))',
                borderBottom: '1px solid var(--gray-6)',
              }}
            />

            {alert.destinations.length > 0 ? (
              <Flex direction="column" gap="2">
                {alert.destinations.map((dest) => (
                  <Flex key={dest.id} align="center" gap="2">
                    <Badge
                      color={dest.isEnabled ? 'green' : 'gray'}
                      variant="soft"
                      size="2"
                    >
                      {getDestinationLabel(dest)}
                    </Badge>
                    {!dest.isEnabled && (
                      <Text size="1" color="gray">
                        (disabled)
                      </Text>
                    )}
                  </Flex>
                ))}
                <Flex justify="end" mt="2">
                  <Dialog.Root
                    open={isAddDestinationOpen}
                    onOpenChange={(open) => {
                      setIsAddDestinationOpen(open);
                      if (!open) resetDestinationDialog();
                    }}
                  >
                    <Dialog.Trigger>
                      <Button variant="soft" size="2">
                        <PlusIcon />
                        Add Destination
                      </Button>
                    </Dialog.Trigger>
                    <Dialog.Content style={{ maxWidth: 450 }}>
                      <Dialog.Title>Add Destination</Dialog.Title>
                      <Dialog.Description size="2" mb="4">
                        Choose where to send notifications when this alert
                        triggers.
                      </Dialog.Description>

                      <Flex direction="column" gap="4">
                        <Box>
                          <Text as="label" size="2" weight="medium" mb="1">
                            Destination Type
                          </Text>
                          <Select.Root
                            value={destinationType}
                            onValueChange={(value) => {
                              setDestinationType(value as 'discord' | 'webhook');
                              setSelectedBotId('');
                              setSelectedChannelId('');
                              setSelectedWebhookId('');
                            }}
                          >
                            <Select.Trigger style={{ width: '100%' }} />
                            <Select.Content>
                              <Select.Item value="discord">
                                <Flex align="center" gap="2">
                                  <DiscordLogoIcon />
                                  Discord Bot
                                </Flex>
                              </Select.Item>
                              <Select.Item value="webhook">
                                <Flex align="center" gap="2">
                                  <Link2Icon />
                                  Webhook
                                </Flex>
                              </Select.Item>
                            </Select.Content>
                          </Select.Root>
                        </Box>

                        {destinationType === 'discord' ? (
                          <>
                            <Box>
                              <Text as="label" size="2" weight="medium" mb="1">
                                Discord Bot
                              </Text>
                              <DiscordBotSelect
                                botId={selectedBotId}
                                onValueChange={setSelectedBotId}
                              />
                            </Box>
                            <Box>
                              <Text as="label" size="2" weight="medium" mb="1">
                                Channel
                              </Text>
                              <DiscordBotChannelSelect
                                botId={selectedBotId}
                                channelId={selectedChannelId}
                                onValueChange={setSelectedChannelId}
                                disabled={!selectedBotId}
                              />
                            </Box>
                          </>
                        ) : (
                          <Box>
                            <Text as="label" size="2" weight="medium" mb="1">
                              Webhook
                            </Text>
                            <WebhookSelect
                              webhookId={selectedWebhookId}
                              onValueChange={setSelectedWebhookId}
                            />
                          </Box>
                        )}
                      </Flex>

                      <Flex gap="3" mt="4" justify="end">
                        <Dialog.Close>
                          <Button variant="soft" color="gray">
                            Cancel
                          </Button>
                        </Dialog.Close>
                        <Button
                          onClick={handleAddDestination}
                          disabled={!canAddDestination || updating}
                        >
                          {updating ? 'Adding...' : 'Add Destination'}
                        </Button>
                      </Flex>
                    </Dialog.Content>
                  </Dialog.Root>
                </Flex>
              </Flex>
            ) : (
              <Flex direction="column" align="center" gap="4" py="6">
                <Box
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--gray-3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BellIcon width={24} height={24} color="var(--gray-9)" />
                </Box>
                <Flex direction="column" align="center" gap="1">
                  <Heading size="3">No destinations configured</Heading>
                  <Text size="2" color="gray" align="center">
                    Add a destination to receive notifications when this alert
                    triggers
                  </Text>
                </Flex>
                <Flex gap="2">
                  <Button variant="soft" size="2">
                    <DiscordLogoIcon />
                    Discord
                  </Button>
                  <Button variant="soft" size="2">
                    <EnvelopeClosedIcon />
                    Email
                  </Button>
                  <Button variant="soft" size="2">
                    <Link2Icon />
                    Webhook
                  </Button>
                  <Button variant="soft" size="2">
                    <ChatBubbleIcon />
                    Slack
                  </Button>
                </Flex>
              </Flex>
            )}
          </Card>
        </Box>
      </Box>
    </DetailPageLayout>
  );
};

export default AlertDetail;
